/**
 * Spec 094: Google AIO-aware response body renderer.
 *
 * Pure presentational component (no hooks, no state, no API). Renders an AIO
 * capture's structure as:
 *   1. Lead answer paragraph (Markdown-rendered).
 *   2. PAA chip list (single-line `?`-terminated sentences before first snippet).
 *   3. Vertical stack of source-snippet cards (each linked to its aio_sources row).
 *   4. Collapsed <details> with the raw_content for debugging.
 *
 * Failure mode: when zero source titles can be located in the body, the entire
 * body falls back to a `<pre>`-wrapped raw view inside the lead block (so the
 * consultant always sees the content, never an error). See FR-9 in spec.md.
 *
 * Imports `DOMAIN_TYPE_COLORS` from ResponseContent.tsx for visual consistency.
 * No new design tokens (per plan.md SC-5).
 */

import ReactMarkdown from 'react-markdown';
import { Icon } from './ui/Icon.js';
import { detectSnippetLanguage, bcp47Language, type DetectedLang } from '../lib/lang-detect.js';

export interface AioSource {
  url: string;
  title: string;
  domain: string;
  is_retrieved?: boolean;
  is_cited?: boolean;
}

interface ParsedAioBody {
  lead: string;
  paa: string[];
  snippets: Array<{ source: AioSource; body: string }>;
  matchedAny: boolean;
}

/**
 * Normalize a string for prefix matching: lowercase + collapse runs of
 * whitespace to a single space + trim. Used for both the title and each
 * candidate line of raw_content.
 */
function normalize(s: string): string {
  return s.toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Heuristic split per clarifications.md Q2/Q3:
 *   - Walk each aio_sources[i].title.
 *   - Normalize title and each line of rawContent.
 *   - Find lines where normalized line starts with normalized title.
 *   - Titles shorter than 6 chars are skipped.
 *   - First match per title becomes that source's snippet header line.
 *   - Sort matches by position; lines before the first match form the lead.
 *   - Inside the lead range, `?`-terminated single-line sentences <200 chars
 *     are PAA questions; the rest is the lead paragraph.
 *   - Each snippet body runs from the line after its header through the line
 *     before the next snippet header (or end of body).
 *   - Output is deterministic — pure function over inputs.
 */
export function parseAioBody(rawContent: string, aioSources: AioSource[]): ParsedAioBody {
  const lines = rawContent.split(/\r?\n/);
  const normLines = lines.map(normalize);

  // For each source, find the first line that starts with its normalized
  // title. Map source-index → line-index. -1 means unmatched.
  const matches: Array<{ srcIdx: number; lineIdx: number }> = [];
  const usedLines = new Set<number>();

  aioSources.forEach((src, srcIdx) => {
    if (!src || typeof src.title !== 'string') return;
    const normTitle = normalize(src.title);
    if (normTitle.length < 6) return;
    for (let lineIdx = 0; lineIdx < normLines.length; lineIdx++) {
      if (usedLines.has(lineIdx)) continue;
      if (normLines[lineIdx].startsWith(normTitle)) {
        matches.push({ srcIdx, lineIdx });
        usedLines.add(lineIdx);
        break;
      }
    }
  });

  // Sort matches by their line position. Snippets are emitted in body-order,
  // not aio_sources-order, so the reading flow matches the rendered text.
  matches.sort((a, b) => a.lineIdx - b.lineIdx);

  if (matches.length === 0) {
    // No titles matched. Fall-safe: treat the entire body as the lead, no
    // PAA, no snippet cards. The consumer can decide whether to use this
    // structure or fall through to the legacy <HighlightedText>.
    return { lead: rawContent, paa: [], snippets: [], matchedAny: false };
  }

  const firstMatchLine = matches[0].lineIdx;

  // Lead range = [0, firstMatchLine). Extract PAA + lead from it.
  const leadRangeLines = lines.slice(0, firstMatchLine);
  const paa: string[] = [];
  const leadLines: string[] = [];
  for (const line of leadRangeLines) {
    const trimmed = line.trim();
    if (trimmed.length === 0) {
      leadLines.push(line);
      continue;
    }
    if (trimmed.endsWith('?') && trimmed.length < 200 && !trimmed.includes('\n')) {
      paa.push(trimmed);
    } else {
      leadLines.push(line);
    }
  }
  const lead = leadLines.join('\n').trim();

  // Snippets: each match's body = lines (lineIdx + 1) ... (next match.lineIdx - 1).
  const snippets: ParsedAioBody['snippets'] = [];
  for (let i = 0; i < matches.length; i++) {
    const { srcIdx, lineIdx } = matches[i];
    const nextLineIdx = i + 1 < matches.length ? matches[i + 1].lineIdx : lines.length;
    const bodyLines = lines.slice(lineIdx + 1, nextLineIdx);
    // Trim leading/trailing blank lines but preserve interior structure.
    let start = 0;
    let end = bodyLines.length;
    while (start < end && bodyLines[start].trim() === '') start++;
    while (end > start && bodyLines[end - 1].trim() === '') end--;
    const body = bodyLines.slice(start, end).join('\n');
    snippets.push({ source: aioSources[srcIdx], body });
  }

  return { lead, paa, snippets, matchedAny: true };
}

/**
 * Render a small EN/DA pill in the snippet card header when the snippet's
 * detected language differs from the brand's primary locale.
 */
function LanguageBadge({ snippetText, brandLocale }: { snippetText: string; brandLocale: string | null }) {
  const brandLang = bcp47Language(brandLocale);
  const detected: DetectedLang = detectSnippetLanguage(snippetText);

  // Only badge when (a) we confidently detected a language and (b) it differs
  // from the brand's primary language. Otherwise the badge would be noise.
  if (detected === 'unknown') return null;
  if (!brandLang) return null;
  if (detected === brandLang) return null;

  const label = detected.toUpperCase();
  const className =
    detected === 'en'
      ? 'inline-flex items-center rounded-md bg-sky-100 px-1.5 py-0.5 text-xs font-semibold text-sky-700'
      : 'inline-flex items-center rounded-md bg-rose-100 px-1.5 py-0.5 text-xs font-semibold text-rose-700';

  return (
    <span
      className={className}
      title={`Detected ${label}; brand locale is ${brandLocale}`}
      data-testid="aio-snippet-lang-badge"
    >
      {label}
    </span>
  );
}

/** Markdown-render the lead block, reusing the prose styling vocabulary from ResponseContent. */
function LeadBlock({ text }: { text: string }) {
  return (
    <div
      className="prose-sm text-sm leading-relaxed text-slate-800 [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:text-slate-900 [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-slate-900 [&_h2]:mt-3 [&_h2]:mb-1.5 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-slate-900 [&_h3]:mt-2 [&_h3]:mb-1 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_li]:mb-0.5 [&_strong]:font-semibold [&_strong]:text-slate-900 [&_a]:text-violet-600 [&_a]:underline"
      data-testid="aio-lead"
    >
      <ReactMarkdown>{text}</ReactMarkdown>
    </div>
  );
}

function PaaList({ paa }: { paa: string[] }) {
  if (paa.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="aio-paa-list">
      {paa.map((q, i) => (
        <span
          key={i}
          className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600"
          data-testid="aio-paa"
        >
          {q}
        </span>
      ))}
    </div>
  );
}

function SnippetCard({
  source,
  body,
  brandLocale,
}: {
  source: AioSource;
  body: string;
  brandLocale: string | null;
}) {
  const domain = source.domain || '';
  return (
    <article
      className="rounded-md border border-slate-200 bg-white p-3"
      data-testid="aio-snippet"
    >
      <div className="mb-1.5 flex items-start justify-between gap-2">
        <div className="flex flex-1 items-center gap-2">
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-semibold text-violet-700 hover:underline"
            data-testid="aio-snippet-title"
          >
            {source.title || source.url}
          </a>
          <Icon name="bx-link-external" className="text-slate-400" />
        </div>
        <div className="flex items-center gap-1.5">
          {domain && (
            <span className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600">
              {domain}
            </span>
          )}
          <LanguageBadge snippetText={body} brandLocale={brandLocale} />
        </div>
      </div>
      <p className="whitespace-pre-wrap text-sm text-slate-700">{body}</p>
    </article>
  );
}

interface AioResponseBodyProps {
  rawContent: string;
  aioSources: AioSource[];
  brandLocale: string | null;
}

export function AioResponseBody({ rawContent, aioSources, brandLocale }: AioResponseBodyProps) {
  const parsed = parseAioBody(rawContent, aioSources);
  const showStructured = parsed.matchedAny;

  return (
    <div className="flex flex-col gap-4" data-testid="aio-response-body">
      {showStructured ? (
        <>
          {parsed.lead && <LeadBlock text={parsed.lead} />}
          <PaaList paa={parsed.paa} />
          {parsed.snippets.length > 0 && (
            <div className="flex flex-col gap-2" data-testid="aio-snippet-list">
              {parsed.snippets.map((s, i) => (
                <SnippetCard
                  key={`${s.source.url}-${i}`}
                  source={s.source}
                  body={s.body}
                  brandLocale={brandLocale}
                />
              ))}
            </div>
          )}
          {/* Fail-safe affordance: if there are AIO sources we couldn't locate
              in the body, show a quiet hint so the consultant knows the right-
              rail Sources panel has the rest. */}
          {aioSources.length > parsed.snippets.length && (
            <p
              className="text-xs text-slate-500"
              data-testid="aio-unmatched-note"
            >
              {aioSources.length - parsed.snippets.length} source(s) with no exact title match in body — see Sources panel.
            </p>
          )}
        </>
      ) : (
        // Zero source-title matches: fall back to showing the raw body as the
        // lead so the consultant still sees the content. This is strictly
        // better than throwing an error or showing nothing.
        <LeadBlock text={rawContent} />
      )}

      <details
        className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs"
        data-testid="aio-raw-response"
      >
        <summary className="cursor-pointer font-medium text-slate-600">
          Raw response
        </summary>
        <pre className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap break-words font-mono text-xs text-slate-700">
          {rawContent}
        </pre>
      </details>
    </div>
  );
}
